/* eslint-disable react-hooks/exhaustive-deps */
import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import male from '../../../../assets/male-big.png';
import Button from 'components/Form/Button';
import withCreatePortal from 'components/HOC/withCreatePortal';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Icon, Icons } from 'components/Icon';
import {
  useDeactiveActivateStaff,
  useGetStaffCustomerList,
  useGetStaffDetails
} from 'common/queries-and-mutations/admin/staff';
import { format } from 'date-fns';
import ConfirmModal from 'components/Modal/ConfirmModal';
import { Loader } from 'components/Loader';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { ListOrder, PaginatedListMeta } from 'types/general.type';
import { UserInfoDto } from 'service/dto/user.dto';
import Badge from 'components/Badge';
import Paginator from 'components/TableWidget/Paginator';
import clsx from 'clsx';
import { getProductBoughtName } from 'utils';

const EnhancedConfirmModal = withCreatePortal(ConfirmModal);

export default function StaffDetails() {
  const [showAskToConfirm, setShowAskToConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExecutingSearch, setIsExecutingSearch] = useState<boolean>(true);
  const [urlParams, setUrlParams] = useSearchParams();
  const currentPage = urlParams.get('page') ?? '1';
  const numberOfItemsPerPage = urlParams.get('items_per_page') || 10;
  const [sorting, setSorting] = useState<SortingState>([]);

  const { staffId } = useParams();

  const { data, isLoading, refetch } = useGetStaffDetails({
    staffId: +staffId!
  });

  const { mutate, isSuccess, isError } = useDeactiveActivateStaff();

  const getPaginationParams = useCallback(() => {
    return {
      page: +currentPage,
      take: +numberOfItemsPerPage,
      order: ListOrder.ASC
    };
  }, [currentPage, numberOfItemsPerPage]);

  const { data: staffCustomerList, isFetching } = useGetStaffCustomerList({
    whereOptions: { staffId },
    paginationOptions: getPaginationParams(),
    enabled: isExecutingSearch
  });

  useEffect(() => {
    if (!isFetching && isExecutingSearch) {
      setIsExecutingSearch(false);
    }
  }, [isExecutingSearch, isFetching]);

  const handleStaffActive = () => {
    setIsSubmitting(true);
    mutate({ staffId: +staffId! });
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      toast(<Alert message="Staff Successfully Updated" type="success" />);
      refetch();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  const columnHelper = createColumnHelper<UserInfoDto>();

  const columns = [
    columnHelper.accessor(row => row.othername, {
      id: 'othername',
      cell: info => (
        <span className="text-xs font-normal">{`${info.row.original.othername} ${info.row.original.surname}`}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Name</span>
      )
    }),
    columnHelper.accessor(row => row.email, {
      id: 'email',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Email</span>
      )
    }),
    columnHelper.accessor(row => row.phoneNumber, {
      id: 'phoneNumber',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">
          Phone Number
        </span>
      )
    }),
    columnHelper.accessor(row => row.planId, {
      id: 'planId',
      cell: info => (
        <span className="text-xs font-normal">
          {getProductBoughtName(info.getValue())}
        </span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">
          Product Bought
        </span>
      )
    }),
    columnHelper.accessor(row => row.createdAt, {
      id: 'createdAt',
      cell: info => (
        <span className="text-xs font-normal">
          {format(new Date(info.getValue()), 'dd MMM yyyy')}
        </span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Date Added</span>
      )
    }),
    columnHelper.accessor(row => row.isActive, {
      id: 'isActive',
      cell: info => (
        <Badge
          size="sm"
          title={info.getValue() === 1 ? 'Active' : 'Inactive'}
          type={info.getValue() === 1 ? 'success' : 'danger'}
          className="text-white"
        />
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Status</span>
      )
    }),
    columnHelper.accessor(row => row.id, {
      id: 'id',
      cell: info => (
        <div>
          <Link
            to={`/admin/customers/details/${info.getValue()}`}
            className="rounded-md bg-primary-900 p-2 px-5 text-white underline"
          >
            View
          </Link>
        </div>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Action</span>
      )
    })
  ];

  const customerData = useMemo<UserInfoDto[]>(() => {
    const normalizedResult: any[] = (staffCustomerList?.data ?? []).map(
      item => ({
        id: item.id,
        email: item.email,
        isActive: item.isActive,
        planId: item.planId,
        surname: item.surname,
        othername: item.othername,
        phoneNumber: item.phoneNumber,
        accountType: item.accountType,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        deletedAt: item.deletedAt
      })
    );
    return normalizedResult;
  }, [staffCustomerList]);

  const listMetaData = (staffCustomerList?.meta ?? {}) as PaginatedListMeta;

  const table = useReactTable({
    data: customerData,
    columns,
    state: {
      sorting
    },
    initialState: {
      pagination: {
        pageSize: +numberOfItemsPerPage
      }
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  const goToNextPage = () => {
    const nextPageValue = currentPage ? `${+currentPage + 1}` : '2';
    urlParams.set('page', nextPageValue);
    setUrlParams(urlParams);
  };

  const goToPreviousPage = () => {
    if (currentPage && +currentPage > 1) {
      const previousPageValue = `${+currentPage - 1}`;
      urlParams.set('page', previousPageValue);
      setUrlParams(urlParams);
    }
  };

  const renderActionButton = useCallback(() => {
    if (data?.isActive) {
      return (
        <Button
          variant="primary"
          size="custom"
          className="h-5 w-[10rem] rounded-[3px] p-2.5 text-sm font-medium"
          label="Deactivate Staff"
          type="button"
          disabled={isSubmitting}
          loading={isSubmitting}
          onClick={() => setShowAskToConfirm(true)}
        ></Button>
      );
    } else {
      return (
        <Button
          variant="primary"
          size="custom"
          className="h-5 w-[10rem] rounded-[3px] bg-[#00B388] p-2.5 text-sm font-medium hover:bg-[#00B388]"
          label="Activate Staff"
          type="button"
          onClick={() => setShowAskToConfirm(true)}
          disabled={isSubmitting}
          loading={isSubmitting}
        ></Button>
      );
    }
  }, [data?.isActive, isSubmitting, isSuccess]);

  if (isLoading && isFetching) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <Breadcrumb title="Staff" />
        <Link to={`/admin/staff`} className="flex items-center gap-4">
          <Icon name={Icons.ArrowLeft} />
          <span className="text-base font-bold text-[#FF6606]">
            Back to Staff
          </span>
        </Link>
      </div>
      <div className="mt-5">
        <div className="mb-5 flex flex-row rounded bg-white">
          <div className="flex w-1/5 flex-col items-center justify-center border-r px-6">
            <div className="flex flex-col items-center justify-center gap-3">
              <img src={male} />
              <p className="text-xl font-bold text-[#353535]">{`${data?.othername} ${data?.surname}`}</p>
            </div>
          </div>
          <div className="w-full">
            <div className="border-b border-[#E5E5E5]">
              <div className="mt-3 grid grid-cols-4 gap-x-16 px-6">
                <div className="col-span-1 px-8 py-6">
                  <p className="text-[10px] font-normal text-[#7A7C87]">Name</p>
                  <p className="mt-1 text-sm font-normal text-[#353535]">
                    {`${data?.othername} ${data?.surname}`}
                  </p>
                </div>
                <div className="col-span-1 px-8 py-6">
                  <p className="text-[10px] font-normal text-[#7A7C87]">
                    E-mail Address
                  </p>
                  <p className="mt-1 text-sm font-normal text-[#353535]">
                    {data?.email}
                  </p>
                </div>
                <div className="col-span-1 px-8 py-6">
                  <p className="text-[10px] font-normal text-[#7A7C87]">
                    Phone Number
                  </p>
                  <p className="mt-1 text-sm font-normal text-[#353535]">
                    {data?.phoneNumber}
                  </p>
                </div>
                <div className="col-span-1 px-8 py-6">
                  <p className="text-[10px] font-normal text-[#7A7C87]">
                    Date Added
                  </p>
                  <p className="mt-1 text-sm font-normal text-[#353535]">
                    {data?.createdAt
                      ? format(new Date(data.createdAt), 'dd MMM yyyy')
                      : 'Unknown'}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-end gap-1">
                {renderActionButton()}
              </div>
            </div>
          </div>
        </div>

        <div className="relative overflow-x-auto bg-white pb-11 sm:rounded-lg">
          <div className="mb-3 flex items-center justify-between border-b">
            <div className="p-2 pl-8 text-left text-lg font-medium text-[#353535]">
              Customers Assigned
            </div>
          </div>
          <div className="">
            {!isFetching && (
              <div className="mt-3 flex flex-col overflow-auto rounded-[5px] bg-white">
                <table
                  className={clsx({
                    'w-full min-w-full': true,
                    'flexs flex-col h-[30rem]':
                      table.getRowModel().rows.length === 0
                  })}
                >
                  <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                      <tr
                        key={headerGroup.id}
                        className="border-b  text-xs text-[#1F4173]"
                      >
                        {headerGroup.headers.map(header => (
                          <th
                            key={header.id}
                            className="p-5 text-left font-medium text-[#1F4173]"
                          >
                            {header.isPlaceholder ? null : (
                              <div
                                {...{
                                  className: header.column.getCanSort()
                                    ? 'cursor-pointer select-none flex'
                                    : '',
                                  onClick:
                                    header.column.getToggleSortingHandler()
                                }}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                                {{
                                  // asc: (
                                  //   <div className="flex flex-row items-center pl-2">
                                  //     <Icon name={Icons.Sort} />
                                  //   </div>
                                  // ),
                                  // desc: (
                                  //   <div className="flex flex-row items-center pl-2">
                                  //     <Icon name={Icons.Sort} />
                                  //   </div>
                                  // )
                                  asc: <></>,
                                  desc: <></>
                                }[header.column.getIsSorted() as string] ??
                                  null}
                              </div>
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.length === 0 && (
                      <tr className="mt-10 pt-10 text-center text-xl font-bold">
                        <td colSpan={7}>No customer found</td>
                      </tr>
                    )}
                    {table.getRowModel().rows.map((row, cellIndex) => (
                      <tr key={row.id} className="">
                        {row.getVisibleCells().map(cell => (
                          <td
                            key={cell.id}
                            className={clsx({
                              'p-5 text-xs': true,
                              'bg-[#F9F9F9]': cellIndex % 2 !== 0
                            })}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {listMetaData && listMetaData.pageCount > 1 && (
                  <div className="mt-5 p-5">
                    <Paginator
                      numberOfPages={listMetaData.pageCount}
                      page={+currentPage}
                      hasNext={listMetaData.hasNextPage}
                      hasPrevious={listMetaData.hasPreviousPage}
                      numberOfItemsPerPage={+numberOfItemsPerPage}
                      setNumberOfItemsPerPage={num => {
                        // setPageSize(num);
                        urlParams.set('items_per_page', num.toString());
                        setUrlParams(urlParams);
                      }}
                      goToNextPage={goToNextPage}
                      goToPreviousPage={goToPreviousPage}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showAskToConfirm && (
        <EnhancedConfirmModal
          content={`Are you sure you want to ${
            data?.isActive ? 'deactivate' : 'activate'
          } this staff?`}
          onConfirm={() => {
            setShowAskToConfirm(false);
            handleStaffActive();
          }}
          onCancel={() => {
            setShowAskToConfirm(false);
          }}
          actionText="Yes"
          cancelText="No"
          title="Action Required"
        />
      )}
    </div>
  );
}
